import React, { useState } from 'react';
import { ResumeData } from '../../../../../types/resume';
import SkillsList from './SkillsList';
import { Code, Users, Globe, Laptop, Plus } from 'lucide-react';

export const SKILL_CATEGORIES = [
  {
    id: 'technical',
    name: 'Technical Skills',
    icon: Code,
    description: 'Programming languages, frameworks, tools',
    examples: 'JavaScript, Python, React, Node.js'
  },
  {
    id: 'soft',
    name: 'Soft Skills',
    icon: Users,
    description: 'Communication, leadership, teamwork',
    examples: 'Leadership, Communication, Problem Solving'
  },
  {
    id: 'languages',
    name: 'Languages',
    icon: Globe,
    description: 'Language proficiency and certifications',
    examples: 'English (Native), Spanish (Fluent), French (Basic)'
  },
  {
    id: 'tools',
    name: 'Tools & Software',
    icon: Laptop,
    description: 'Software, platforms, applications',
    examples: 'Adobe Suite, Microsoft Office, Git'
  }
];

interface Props {
  data: ResumeData;
  onUpdate: (data: Partial<ResumeData>) => void;
}

export default function SkillsForm({ data, onUpdate }: Props) {
  const [selectedCategory, setSelectedCategory] = useState(SKILL_CATEGORIES[0].id);
  const skills = data.skills || [];

  const addSkill = () => {
    onUpdate({
      skills: [...skills, {
        id: crypto.randomUUID(),
        name: '',
        level: 'intermediate',
        category: selectedCategory
      }]
    });
  };

  const handleSkillsUpdate = (updatedSkills: typeof skills) => {
    const otherSkills = skills.filter(skill => skill.category !== selectedCategory);
    onUpdate({ skills: [...otherSkills, ...updatedSkills] });
  };

  return (
    <div className="space-y-6">
      <div className="text-center max-w-2xl mx-auto mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Skills & Expertise</h2>
        <p className="mt-2 text-gray-600">
          Add your technical and professional skills. Group them by category and rate your proficiency
          level to give employers a clear picture of your capabilities.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className="flex flex-wrap gap-2">
            {SKILL_CATEGORIES.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors
                  ${selectedCategory === category.id
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }
                `}
              >
                <category.icon className="h-4 w-4" />
                {category.name}
              </button>
            ))}
          </div>

          {SKILL_CATEGORIES.map(category => (
            selectedCategory === category.id && (
              <div key={category.id} className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-1">{category.name}</h3>
                <p className="text-sm text-gray-600 mb-2">{category.description}</p>
                <p className="text-sm text-gray-500">Examples: {category.examples}</p>
              </div>
            )
          ))}
        </div>
      
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-medium text-gray-900">Your Skills</h3>
            <button
              onClick={addSkill}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700"
            >
              <Plus className="h-4 w-4" />
              Add Skill
            </button>
          </div>
          
          <SkillsList
            skills={skills.filter(skill => skill.category === selectedCategory)}
            onUpdate={handleSkillsUpdate}
          />
        </div>
      </div>
    </div>
  );
}